import Image from 'components/common/Image'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFile } from '@fortawesome/pro-regular-svg-icons'
import { PropsWithChildren, useEffect } from 'react'
import {
  useThreadFileGetURLQuery,
  useThreadFileUploadURLQuery
} from 'graphql/generated'
import useS3FileUpload, { TFileUploadStatus } from './useS3FileUpload'
import { Typography } from 'components/common'
import { twMerge } from 'tailwind-merge'

type TMediaReadyHandler = CallbackWithParam<TFileUploadStatus>

interface IProps {
  file: File
  onFileStatusChange: TMediaReadyHandler
  discussionBoardId: string
  sizeLimitMB?: number
  showFileUpload?: boolean
}

export const FileWrapper: React.FC<
  PropsWithChildren<{ className?: string; size?: 'sm' | 'md' | 'lg' }>
> = ({ children, className, size = 'sm' }) => {
  return (
    <div
      className={twMerge(
        'group relative w-24 h-24 flex flex-col items-center justify-center rounded-lg',
        size === 'sm' && 'w-24 h-24',
        size === 'md' && 'w-36 h-36',
        size === 'lg' && 'w-48 h-48',
        className
      )}
    >
      {children}
    </div>
  )
}

export default function FileUpload({
  file,
  discussionBoardId,
  sizeLimitMB,
  onFileStatusChange
}: IProps) {
  const { upload, uploadStatus } = useS3FileUpload(sizeLimitMB)

  const { loading: gettingUploadUrl } = useThreadFileUploadURLQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    variables: {
      discussionBoardId,
      fileName: encodeURIComponent(file.name.trim()).replaceAll(/%../gm, '-'),
      fileType: file.type
    },
    onCompleted: async ({ threadFileUploadUrl }) => {
      if (uploadStatus.started || uploadStatus.error) {
        return
      }
      await upload(file, threadFileUploadUrl.url, threadFileUploadUrl.key)
    }
  })

  useEffect(() => {
    onFileStatusChange(uploadStatus)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadStatus])

  const { data: getUrl, loading: gettingGetUrl } = useThreadFileGetURLQuery({
    variables: {
      discussionBoardId,
      key: uploadStatus.s3Key || ''
    },
    skip: !uploadStatus.isFileReady || !uploadStatus.s3Key
  })

  const progress = uploadStatus?.progress
  const started = uploadStatus?.started || gettingUploadUrl || gettingGetUrl
  const isErrored = !!uploadStatus?.error
  const isReady = uploadStatus?.isFileReady
  const isImage = uploadStatus?.type?.includes('image')

  if (isErrored) {
    return (
      <FileWrapper>
        <Typography size="sm" color="gray" className="px-2">
          {uploadStatus.error}
        </Typography>
      </FileWrapper>
    )
  }

  return (
    <FileWrapper>
      {isReady && getUrl && (
        <>
          {isImage && getUrl.threadFileGetUrl.url && (
            <Image
              src={getUrl.threadFileGetUrl.url || ''}
              alt={`File ${uploadStatus.name}`}
              fill
              sizes="200px"
              className="object-cover rounded-md overflow-hidden"
            />
          )}

          {!isImage && (
            <Typography size="sm" color="gray" className="px-2">
              <FontAwesomeIcon icon={faFile} /> {file.name}
            </Typography>
          )}
        </>
      )}

      {!isReady && started && (
        <div className="w-full h-full flex flex-col px-4 gap-4 justify-center items-center">
          <div className="text-center text-xs text-gray-600">
            Uploading {uploadStatus.name}
          </div>

          {!!progress && (
            <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
              <div
                className="bg-blue-600 h-2.5 rounded-full"
                style={{
                  width: `${progress}%`
                }}
              />
            </div>
          )}
        </div>
      )}
    </FileWrapper>
  )
}
